/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  TableCell,
  TableRow,
  TableBody,
  Table,
  TableContainer,
  Paper,
  Button,
  Box,
  Checkbox,
  Typography,
  Tooltip,
  Popover,
  Slider,
  Divider,
  TableHead,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { SketchPicker } from 'react-color';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  MANAGE_ASSETS_COLUMNS,
  MANAGE_APP_THEME_COLUMNS,
  MANAGE_THEME_COLUMNS,
} from '../../config/module-configs/assets.config';
import {
  getChannelAssetData,
  updateChannelAssetData,
} from '../../services/apps.service';
import ConfirmPopup from '../common/confirm-popup.component';
import S3ImageUpload from '../common/S3ImageUpload';
import useAccess from '../../hooks/access.hook';

const ManageAppContentAssets = ({
  channelId,
  channelCode,
  handleRefreshData,
  viewMode,
}) => {
  const assetsColumns = MANAGE_ASSETS_COLUMNS;
  const themeColumns = MANAGE_THEME_COLUMNS;
  const appThemeColumns = MANAGE_APP_THEME_COLUMNS;
  const { hasOperationAccess } = useAccess();

  const [showConfirmDailog, setShowConfirmDailog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showEditConfirmDailog, setShowEditConfirmDailog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentColorField, setCurrentColorField] = useState(null);
  const splashImageTimeStamp = useRef(null);

  const { control, handleSubmit, setValue, getValues, watch } = useForm({});

  useEffect(() => {
    getChannelAssetData(channelId)
      .then((res) => {
        splashImageTimeStamp.current = res.data?.splashImageTimeStamp || 0;
        Object.keys(res.data).forEach((key) => {
          setValue(`${key}.value`, res.data[key]);
        });
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, []);

  const handleOpen = (event, fieldName) => {
    setAnchorEl(event.currentTarget);
    setCurrentColorField(fieldName);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCurrentColorField(null);
  };

  const isPopoverOpen = Boolean(anchorEl);

  const handleSaveData = () => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    const payload = getValues();
    payload.splashImageTimeStamp = {
      createForAllChannel: false,
      value: splashImageTimeStamp.current || 0,
    };

    updateChannelAssetData(channelId, payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Assets & Theme details updated successfully.',
        });
        setFormSubmitted(false);
        setShowConfirmDailog(false);
        setShowEditConfirmDailog(false);
        handleRefreshData();
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
        setShowConfirmDailog(false);
        setShowEditConfirmDailog(false);
      });
  };

  const onFormSubmit = (data) => {
    if (Object.values(data).some((item) => item.createForAllChannel)) {
      setShowConfirmDailog(true);
    } else if (!channelId) {
      setShowEditConfirmDailog(true);
    } else {
      handleSaveData();
    }
  };

  return (
    <>
      {loading && <Loading />}

      {!loading && (
        <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
          <Box
            component={Paper}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '75vh',
              overflow: 'hidden',
              p: 2,
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                padding: 2,
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="20%" sx={{ border: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ textAlign: 'center' }}
                        >
                          Label
                        </Typography>
                      </TableCell>
                      <TableCell width="70%" sx={{ border: 0 }}>
                        {' '}
                        <Typography
                          variant="subtitle1"
                          sx={{ textAlign: 'center' }}
                        >
                          Value
                        </Typography>
                      </TableCell>
                      {!viewMode && !!channelId && (
                        <TableCell width="10%" sx={{ border: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ textAlign: 'center' }}
                          >
                            Update for all channels
                          </Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ border: 0 }}>
                        <Divider
                          textAlign="center"
                          variant="fullWidth"
                          sx={{ fontWeight: 'bold', my: 1 }}
                        >
                          Assets
                        </Divider>
                      </TableCell>
                    </TableRow>
                    {assetsColumns.map((c) => (
                      <TableRow key={`col-${c.id}`}>
                        <TableCell
                          width="20%"
                          variant="head"
                          sx={{ border: 0, verticalAlign: 'center' }}
                        >
                          {c.label}
                        </TableCell>
                        <TableCell
                          width="70%"
                          variant="body"
                          sx={{ border: 0, verticalAlign: 'top' }}
                        >
                          <Controller
                            name={`${c.dataKey}.value`}
                            control={control}
                            defaultValue=""
                            render={({ field }) => {
                              if (c.type === 'COLOR') {
                                return (
                                  <>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      <Button
                                        variant="outlined"
                                        onClick={(e) =>
                                          handleOpen(e, `${c.dataKey}.value`)
                                        }
                                        sx={{
                                          backgroundColor: field.value,
                                          width: '36px',
                                          height: '36px',
                                          minWidth: '36px',
                                          borderRadius: '4px',
                                          padding: 0,
                                          border: `1px solid ${
                                            field.value || '#ccc'
                                          }`,
                                          '&:hover': {
                                            backgroundColor:
                                              field.value || '#fff',
                                            border: `1px solid ${
                                              field.value || '#ccc'
                                            }`,
                                          },
                                        }}
                                      />

                                      <Typography variant="body2">
                                        {field?.value.toUpperCase()}
                                      </Typography>
                                    </Box>
                                    {c?.text && (
                                      <Box color="gray" fontSize="12px" mt={2}>
                                        {c?.text}
                                      </Box>
                                    )}

                                    <Popover
                                      open={
                                        isPopoverOpen &&
                                        currentColorField ===
                                          `${c.dataKey}.value`
                                      }
                                      anchorEl={anchorEl}
                                      onClose={handleClose}
                                      anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                      }}
                                    >
                                      <SketchPicker
                                        color={field.value}
                                        onChange={(newColor) => {
                                          const hexColor = `${newColor.hex}`;
                                          setValue(
                                            `${c.dataKey}.value`,
                                            hexColor
                                          );
                                        }}
                                      />
                                    </Popover>
                                  </>
                                );
                              }
                              return (
                                <S3ImageUpload
                                  s3Folder={channelCode}
                                  value={field.value}
                                  updateImage={(v) => {
                                    field.onChange(v);
                                    setValue(`${c.dataKey}.value`, v);
                                  }}
                                  allowedSize={
                                    c.dataKey === 'favIconImage'
                                      ? [[16, 16]]
                                      : c.dataKey === 'logoImage'
                                      ? [[216, 50]]
                                      : c.dataKey === 'splashImage'
                                      ? [[1920, 1080]]
                                      : c.dataKey === 'logoSquareImage'
                                      ? [[512, 512]]
                                      : null
                                  }
                                  aspectRatio={
                                    c.dataKey === 'homePageHeroImage' ||
                                    c.dataKey === 'browsePageBgImage'
                                      ? '1.77'
                                      : null
                                  }
                                  setSnackbarInfo={setSnackbarInfo}
                                  showAsPerOpacity={
                                    c.dataKey === 'homePageHeroImage'
                                  }
                                  opacity={watch(
                                    `homePageHeroImageOpacity.value`
                                  )}
                                  isPackage={c.dataKey === 'rokuPackage'}
                                  isSplash={c.dataKey === 'splashImage'}
                                  splashImageSuccess={() => {
                                    splashImageTimeStamp.current = Math.ceil(
                                      Date.now() / 1000
                                    );
                                  }}
                                  viewMode={viewMode}
                                />
                              );
                            }}
                          />
                          {!viewMode && c.dataKey === 'homePageHeroImage' && (
                            <Controller
                              name="homePageHeroImageOpacity.value"
                              control={control}
                              defaultValue={1}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    mt: 2,
                                  }}
                                >
                                  <Typography variant="body2">
                                    Opacity
                                  </Typography>
                                  <Slider
                                    value={field.value * 100}
                                    onChange={(_, newValue) =>
                                      field.onChange(newValue / 100)
                                    }
                                    min={0}
                                    max={100}
                                    step={1}
                                    sx={{ width: 200 }}
                                  />
                                  <Typography variant="body2">
                                    {Math.round(field.value * 100)}%
                                  </Typography>
                                </Box>
                              )}
                            />
                          )}
                          {(c.dataKey === 'homePageHeroImage' ||
                            c.dataKey === 'browsePageBgImage') && (
                            <Box color="gray" fontSize="12px">
                              Note: Upload image in 16:9 aspect ratio (1920 x
                              1080 px) for better experience.
                            </Box>
                          )}
                          {c.dataKey === 'favIconImage' && (
                            <Box color="gray" fontSize="12px">
                              Note: Upload fav icon in 1:1 aspect ratio (16 x 16
                              px) for better experience.
                            </Box>
                          )}
                          {c.dataKey === 'logoImage' && (
                            <Box color="gray" fontSize="12px">
                              Note: Upload logo in 108:25 aspect ratio (216 x 50
                              px) for better experience.
                            </Box>
                          )}
                          {c.dataKey === 'splashImage' && (
                            <Box color="gray" fontSize="12px">
                              Note: Upload a splash image in a 16:9 aspect ratio
                              (1920 X 1080 px) for the best experience. Only PNG
                              files are supported.
                            </Box>
                          )}
                          {c.dataKey === 'rokuPackage' && (
                            <Box color="gray" fontSize="12px">
                              Note: Upload pkg (.pkg) file.
                            </Box>
                          )}
                          {c.dataKey === 'logoSquareImage' && (
                            <Box color="gray" fontSize="12px">
                              Note: Upload sqaure logo image in 1:1 aspect ratio
                              (512 x 512 px) for better experience.
                            </Box>
                          )}
                        </TableCell>
                        {!viewMode && !!channelId && (
                          <TableCell
                            width="10%"
                            variant="body"
                            sx={{ border: 0, verticalAlign: 'top' }}
                          >
                            <Controller
                              name={`${c.dataKey}.createForAllChannel`}
                              control={control}
                              defaultValue={false}
                              render={({ field: { onChange, value } }) => (
                                <Tooltip title="update for all channels" arrow>
                                  <Checkbox
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                    }}
                                    disabled={c.type === 'IMAGE'}
                                    onChange={onChange}
                                    checked={value || false}
                                  />
                                </Tooltip>
                              )}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ border: 0 }}>
                        <Divider
                          textAlign="center"
                          variant="fullWidth"
                          sx={{ fontWeight: 'bold', mt: 4 }}
                        >
                          Theme
                        </Divider>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ border: 0 }}>
                        <Box color="red" fontSize="small" mb={1}>
                          Theme settings automatically applied to website.
                        </Box>
                      </TableCell>
                    </TableRow>
                    {themeColumns.map((c) => (
                      <TableRow key={`col-${c.id}`}>
                        <TableCell
                          width="20%"
                          variant="head"
                          sx={{ border: 0, verticalAlign: 'center' }}
                        >
                          {c.label}
                        </TableCell>
                        <TableCell
                          width="70%"
                          variant="body"
                          sx={{ border: 0, verticalAlign: 'top' }}
                        >
                          <Controller
                            name={`${c.dataKey}.value`}
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Button
                                    variant="outlined"
                                    onClick={(e) =>
                                      handleOpen(e, `${c.dataKey}.value`)
                                    }
                                    sx={{
                                      backgroundColor: field.value,
                                      width: '36px',
                                      height: '36px',
                                      minWidth: '36px',
                                      borderRadius: '4px',
                                      padding: 0,
                                      border: `1px solid ${
                                        field.value || '#ccc'
                                      }`,
                                      '&:hover': {
                                        backgroundColor: field.value || '#fff',
                                        border: `1px solid ${
                                          field.value || '#ccc'
                                        }`,
                                      },
                                    }}
                                    disabled={viewMode}
                                  />

                                  <Typography variant="body2">
                                    {field?.value.toUpperCase()}
                                  </Typography>
                                </Box>
                                {c?.text && (
                                  <Box color="gray" fontSize="12px" mt={2}>
                                    {c?.text}
                                  </Box>
                                )}

                                <Popover
                                  open={
                                    isPopoverOpen &&
                                    currentColorField === `${c.dataKey}.value`
                                  }
                                  anchorEl={anchorEl}
                                  onClose={handleClose}
                                  anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                  }}
                                >
                                  <SketchPicker
                                    color={field.value}
                                    onChange={(newColor) => {
                                      const hexColor = `${newColor.hex}`;
                                      setValue(`${c.dataKey}.value`, hexColor);
                                    }}
                                  />
                                </Popover>
                              </>
                            )}
                          />
                          {c.dataKey === 'homePageHeroImage' && (
                            <Controller
                              name="homePageHeroImageOpacity.value"
                              control={control}
                              defaultValue={1}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    mt: 2,
                                  }}
                                >
                                  <Typography variant="body2">
                                    Opacity
                                  </Typography>
                                  <Slider
                                    value={field.value * 100}
                                    onChange={(_, newValue) =>
                                      field.onChange(newValue / 100)
                                    }
                                    min={0}
                                    max={100}
                                    step={1}
                                    sx={{ width: 200 }}
                                  />
                                  <Typography variant="body2">
                                    {Math.round(field.value * 100)}%
                                  </Typography>
                                </Box>
                              )}
                            />
                          )}
                          {(c.dataKey === 'homePageHeroImage' ||
                            c.dataKey === 'browsePageBgImage') && (
                            <Box color="gray" fontSize="12px">
                              Note: Upload image in 16:9 aspect ratio (1920 x
                              1080 px) for better experience.
                            </Box>
                          )}
                          {c.dataKey === 'favIconImage' && (
                            <Box color="gray" fontSize="12px">
                              Note: Upload fav icon in 1:1 aspect ratio (16 x 16
                              px) for better experience.
                            </Box>
                          )}
                          {c.dataKey === 'logoImage' && (
                            <Box color="gray" fontSize="12px">
                              Note: Upload logo in 108:25 aspect ratio (216 x 50
                              px) for better experience.
                            </Box>
                          )}
                          {c.dataKey === 'splashImage' && (
                            <Box color="gray" fontSize="12px">
                              Note: Upload a splash image in a 16:9 aspect ratio
                              (1920 x 1080 px) for the best experience. Only PNG
                              files are supported.
                            </Box>
                          )}
                          {c.dataKey === 'rokuPackage' && (
                            <Box color="gray" fontSize="12px">
                              Note: Upload pkg (.pkg) file.
                            </Box>
                          )}
                          {c.dataKey === 'logoSquareImage' && (
                            <Box color="gray" fontSize="12px">
                              Note: Upload sqaure logo image in 1:1 aspect ratio
                              (512 x 512 px) for better experience.
                            </Box>
                          )}
                        </TableCell>
                        {!viewMode && !!channelId && (
                          <TableCell
                            width="10%"
                            variant="body"
                            sx={{ border: 0, verticalAlign: 'top' }}
                          >
                            <Controller
                              name={`${c.dataKey}.createForAllChannel`}
                              control={control}
                              defaultValue={false}
                              render={({ field: { onChange, value } }) => (
                                <Tooltip title="update for all channels" arrow>
                                  <Checkbox
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                    }}
                                    disabled={c.type === 'IMAGE'}
                                    onChange={onChange}
                                    checked={value || false}
                                  />
                                </Tooltip>
                              )}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ border: 0 }}>
                        <Divider
                          textAlign="center"
                          variant="fullWidth"
                          sx={{ fontWeight: 'bold', mt: 4 }}
                        >
                          Apply Theme to CTV Apps
                        </Divider>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ border: 0 }}>
                        <Box color="red" fontSize="small" mb={1}>
                          Theme settings automatically applied to CTV Apps.
                        </Box>
                      </TableCell>
                    </TableRow>
                    {appThemeColumns.map((c) => (
                      <TableRow key={`col-${c.id}`}>
                        <TableCell
                          width="20%"
                          variant="head"
                          sx={{ border: 0, verticalAlign: 'center' }}
                        >
                          {c.label}
                        </TableCell>
                        <TableCell
                          width="70%"
                          variant="body"
                          sx={{ border: 0, verticalAlign: 'top' }}
                        >
                          <Controller
                            name={`${c.dataKey}.value`}
                            control={control}
                            defaultValue=""
                            render={({ field }) => {
                              if (c.type === 'COLOR') {
                                return (
                                  <>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      <Button
                                        variant="outlined"
                                        onClick={(e) =>
                                          handleOpen(e, `${c.dataKey}.value`)
                                        }
                                        sx={{
                                          backgroundColor: field.value,
                                          width: '36px',
                                          height: '36px',
                                          minWidth: '36px',
                                          borderRadius: '4px',
                                          padding: 0,
                                          border: `1px solid ${
                                            field.value || '#ccc'
                                          }`,
                                          '&:hover': {
                                            backgroundColor:
                                              field.value || '#fff',
                                            border: `1px solid ${
                                              field.value || '#ccc'
                                            }`,
                                          },
                                        }}
                                        disabled={viewMode}
                                      />

                                      <Typography variant="body2">
                                        {field?.value.toUpperCase()}
                                      </Typography>
                                    </Box>
                                    {c?.text && (
                                      <Box color="gray" fontSize="12px" mt={2}>
                                        {c?.text}
                                      </Box>
                                    )}

                                    <Popover
                                      open={
                                        isPopoverOpen &&
                                        currentColorField ===
                                          `${c.dataKey}.value`
                                      }
                                      anchorEl={anchorEl}
                                      onClose={handleClose}
                                      anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                      }}
                                    >
                                      <SketchPicker
                                        color={field.value}
                                        onChange={(newColor) => {
                                          const hexColor = `${newColor.hex}`;
                                          setValue(
                                            `${c.dataKey}.value`,
                                            hexColor
                                          );
                                        }}
                                      />
                                    </Popover>
                                  </>
                                );
                              }
                              return null;
                            }}
                          />
                        </TableCell>
                        {!viewMode && !!channelId && (
                          <TableCell
                            width="10%"
                            variant="body"
                            sx={{ border: 0, verticalAlign: 'top' }}
                          >
                            <Controller
                              name={`${c.dataKey}.createForAllChannel`}
                              control={control}
                              defaultValue={false}
                              render={({ field: { onChange, value } }) => (
                                <Tooltip title="update for all channels" arrow>
                                  <Checkbox
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                    }}
                                    disabled={c.type === 'IMAGE'}
                                    onChange={onChange}
                                    checked={value || false}
                                  />
                                </Tooltip>
                              )}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box
              sx={{
                padding: 2,
                borderTop: '1px solid #ddd',
                textAlign: 'center',
                backgroundColor: 'white',
              }}
            >
              {!viewMode &&
                ((channelId === 0 &&
                  hasOperationAccess('global_assets', 'edit')) ||
                  channelId > 0) && (
                  <Button
                    type="submit"
                    form="add-edit-form"
                    color="primary"
                    variant="contained"
                    disabled={loading || formSubmitted}
                  >
                    Save
                  </Button>
                )}
            </Box>
          </Box>
        </form>
      )}

      {showConfirmDailog && (
        <ConfirmPopup
          title="Update for all channels confirmation"
          message={`You have selected 'update for all channels'. Do you want to update assets & theme for all channels? You can not undo this action!`}
          onCancel={() => setShowConfirmDailog(false)}
          onSuccess={handleSaveData}
          successButtonText="Proceed"
          cancelButtonText="Cancel"
          showConfirmationInput
          confirmationInputText="confirm"
        />
      )}
      {showEditConfirmDailog && (
        <ConfirmPopup
          title="Update assets confirmation"
          message="Are you sure you want to update assets data? You can not undo this action!"
          onCancel={() => setShowEditConfirmDailog(false)}
          onSuccess={handleSaveData}
          successButtonText="Proceed"
          cancelButtonText="Cancel"
          showConfirmationInput
          confirmationInputText="confirm"
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ManageAppContentAssets.propTypes = {
  channelId: PropTypes.number.isRequired,
  channelCode: PropTypes.string,
  handleRefreshData: PropTypes.func,
  viewMode: PropTypes.bool,
};

ManageAppContentAssets.defaultProps = {
  channelCode: '',
  handleRefreshData: () => {},
  viewMode: false,
};

export default ManageAppContentAssets;
